import React, { createContext, useContext, useState, ReactNode, useCallback, useRef } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  ModalProps,
} from "@heroui/react";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import { IoClose } from "react-icons/io5";

type ModalContextType = {
  openModal: (
    content: ReactNode,
    title?: string,
    modalProps?: Omit<ModalProps, "children"> | undefined
  ) => Promise<unknown>;
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);
  const [modalTitle, setModalTitle] = useState<string | undefined>(undefined);
  const [props, setProps] = useState<Omit<ModalProps, "children">>();
  const resolveRef = useRef<(value: void | PromiseLike<void>) => void>();
  const openModal = useCallback(
    (content: ReactNode, title?: string, modalProps?: Omit<ModalProps, "children"> | undefined) => {
      setModalContent(content);
      setModalTitle(title);
      setProps(modalProps);
      onOpen();
      return new Promise((res) => {
        resolveRef.current = res;
      });
    },
    []
  );

  const closeModal = useCallback(() => {
    onClose();
    setModalContent(null);
    setModalTitle(undefined);
    setProps(undefined);
    if (resolveRef.current) {
      resolveRef.current();
      resolveRef.current = undefined;
    }
  }, []);
  const t = useTranslations();
  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        size="sm"
        scrollBehavior="inside"
        classNames={{
          body: "bg-[#021517]",
          header: "bg-[#0A2A27] shadow-lg rounded-t-lg",
          closeButton: clsx("hover:bg-white/5 active:bg-white/10 !outline-none my-auto top-auto"),
        }}
        className="z-[101]"
        placement="center"
        radius="lg"
        hideCloseButton
        {...props}
      >
        <ModalContent>
          {(close) => (
            <>
              {modalTitle && (
                <ModalHeader className="flex items-center justify-between">
                  {modalTitle} <IoClose size={24} role="button" onClick={close} className="text-white/50" />
                </ModalHeader>
              )}
              <ModalBody>{modalContent}</ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
